import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { BookingsLedgerTable, Breadcrumbs, Table } from "../components";
import useApiService from "../hooks/useApiService";
import { parseAmount } from "../services/utils";

function BookingsPage() {
  const bookingsTableColumns = [
    { id: "plot_number", name: "Plot No." },
    { id: "company_name", name: "Company Name" },
    { id: "project_name", name: "Project Name" },
    { id: "agent_name", name: "Agent Name" },
    { id: "customer_name", name: "Customer Name" },
    { id: "phase_number", name: "Phase No." },
    { id: "status", name: "Status" },
    { id: "action", name: "More Details" },
  ];

  const [bookings, setBookings] = useState([]);
  const { get } = useApiService();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [bookedPlotData, setBookedPlotData] = useState({});
  const navigate = useNavigate();
  const [agentId, setAgentId] = useState(null);
  const [customerId, setCustomerId] = useState(null);

  useEffect(() => {
    const id = searchParams.get("id");
    if (id) setSelectedBookingId(id);
    if (searchParams.get("agent")) setAgentId(searchParams.get("agent"));
    if (searchParams.get("customer")) setCustomerId(searchParams.get("customer"));
  }, []);

  const parseStatus = (status) => {
    switch (status) {
      case "ongoing":
        return "On-going";
      case "completed":
        return "Completed";
      case "cancelled":
        return "Canceled";
      default:
        return status;
    }
  };

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const response = await get("/api/bookings");
        if (response.status && response.data) {
          setBookings(response.data.map((booking) => ({ ...booking, status: parseStatus(booking.status) })));
          return;
        }
        throw new Error("Unable to fetch bookings");
      } catch (error) {
        toast.error("Unable to fetch bookings. Please try again");
      }
    };
    fetchBookings();
  }, []);

  const fetchBookedPlot = async () => {
    try {
      const response = await get(`/api/bookings/${selectedBookingId}`);
      if (response.status && response.data) {
        const plotData = response.data;
        const plotAmount = `${plotData.plot_amount}`.replaceAll(",", "").replace(".00", "");
        const companyCommissionPercent = `${plotData.company_commission}`.replaceAll(",", "").replace(".00", "");
        const companyCommission = plotAmount * (companyCommissionPercent / 100);
        setLedgerData(plotData.ledger);
        const waiveOff = `${plotData.waive_off_amount}`.replaceAll(",", "");
        setBookedPlotData({
          ...plotData,
          status: parseStatus(plotData.status),
          plot_area: plotData.plot_area.replace(/\.00|,|sq\.ft/g, "").trim(),
          company_commission: companyCommission,
          sold_at: plotData.status === "Completed" ? plotData.total_amount_paid : +plotAmount - +waiveOff,
          selling_price: +plotAmount,
        });
        return;
      }
      throw new Error("Unable to fetch booked plot");
    } catch (error) {
      toast.error("Unable to fetch booked plot. Please try again");
    }
  };
  const [ledgerData, setLedgerData] = useState([]);
  useEffect(() => {
    if (selectedBookingId) fetchBookedPlot();
  }, [selectedBookingId]);

  const handleViewBookingAction = (bookingId) => {
    const selectedBooking = bookings.find((item) => item.id === bookingId);
    if (selectedBooking.status.toLowerCase() === "canceled" || selectedBooking.status.toLowerCase() === "cancelled") {
      navigate(`/canceled-plot-request?id=${decodeURIComponent(bookingId)}`);
      return;
    }
    setSearchParams({ id: bookingId });
    setSelectedBookingId(bookingId);
    setAgentId(null);
    setCustomerId(null);
  };

  const breadcrumbs = [];
  if (agentId) {
    breadcrumbs.push({ name: "Agent", link: `/agents?id=${agentId}` });
  } else if (customerId) {
    breadcrumbs.push({ name: "Customer", link: `/customers?id=${customerId}` });
  }
  if (selectedBookingId) {
    breadcrumbs.push({
      name: "Bookings",
      link: `/bookings?id=${selectedBookingId}${
        agentId ? `&agent=${agentId}` : customerId ? `&customer=${customerId}` : ""
      }`,
    });
  } else {
    breadcrumbs.push({ name: "Bookings", link: `/bookings` });
  }

  const detailsRef = useRef();
  useEffect(() => {
    if (selectedBookingId && detailsRef.current)
      detailsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [selectedBookingId]);

  const renderDataRow = (label, value) => {
    return (
      <div className="row">
        <span className="label">{label}</span>
        <span className="value">{value}</span>
      </div>
    );
  };

  return (
    <>
      <Breadcrumbs breadcrumbs={breadcrumbs} />
      <Table
        columns={bookingsTableColumns}
        data={bookings}
        title="Bookings"
        setSelectedId={setSelectedBookingId}
        handleAction={handleViewBookingAction}
      />
      {selectedBookingId && bookedPlotData && (
        <main className="section_wrapper" ref={detailsRef}>
          <section className="section_wrapper">
            <section className="section_title_wrapper">
              <h2 className="section_title">Plot Number {bookedPlotData.plot_number}</h2>
              <div
                className={`status ${
                  bookedPlotData.status === "On-going"
                    ? "status_on-going"
                    : bookedPlotData.status === "Completed"
                    ? "status_completed"
                    : ""
                } `}
              >
                {bookedPlotData.status}
              </div>
            </section>
            <section className="section grid_3">
              <section className="column">
                {renderDataRow("Company name", bookedPlotData.company_name)}
                {renderDataRow("Project name", bookedPlotData.project_name)}
                {renderDataRow("Customer name", bookedPlotData.customer_name)}
                {renderDataRow("Agent name", bookedPlotData.agent_name)}
                {renderDataRow("Phase number", bookedPlotData.phase_number)}
                {renderDataRow("Plot number", bookedPlotData.plot_number)}
                {renderDataRow("Selling price", parseAmount(bookedPlotData.selling_price))}
                {renderDataRow("Sold at", parseAmount(bookedPlotData.sold_at))}
                {bookedPlotData.status === "Completed" ? (
                  <>
                    {renderDataRow("Total Waive off", parseAmount(bookedPlotData.waive_off_amount))}
                    {renderDataRow("Token amount", parseAmount(bookedPlotData.token))}
                  </>
                ) : (
                  <>
                    {renderDataRow("Plot area", `${bookedPlotData.plot_area} sq.ft`)}
                    {renderDataRow("Rate/sq.ft", parseAmount(bookedPlotData.rate_per_sqft))}
                  </>
                )}
              </section>
              <div className="vertical_divider"></div>
              <section className="column">
                {renderDataRow("Booking advance", parseAmount(bookedPlotData.booking_advance))}
                {bookedPlotData.status === "Completed" ? (
                  <>
                    {renderDataRow("Plot area", `${bookedPlotData.plot_area} sq.ft`)}
                    {renderDataRow("Rate/sq.ft", parseAmount(bookedPlotData.rate_per_sqft))}
                    {renderDataRow("Booking Advance", parseAmount(bookedPlotData.booking_advance))}
                  </>
                ) : (
                  <>
                    {renderDataRow("Token decided on plot", parseAmount(bookedPlotData.token))}
                    {bookedPlotData.payment_type === "emi" && (
                      <>
                        {renderDataRow(
                          "EMI chosen",
                          `${bookedPlotData.emi_chosen} ${bookedPlotData.emi_chosen > 1 ? "months" : "month"}`
                        )}
                        {renderDataRow("Each EMI value", parseAmount(bookedPlotData.emi_value))}
                      </>
                    )}
                  </>
                )}
                {bookedPlotData.payment_type === "downpayment" &&
                  renderDataRow("Down payment", parseAmount(bookedPlotData.down_payment))}
                {bookedPlotData.status === "Completed" &&
                  bookedPlotData.payment_type === "emi" &&
                  renderDataRow(
                    "EMI selected",
                    `${bookedPlotData.emi_chosen} ${bookedPlotData.emi_chosen > 1 ? "months" : "month"}`
                  )}
                {bookedPlotData.status === "On-going" &&
                  renderDataRow("Waive off amount", parseAmount(bookedPlotData.waive_off_amount))}
                {renderDataRow(
                  `Agent commission${bookedPlotData.status === "Completed" && " received"}`,
                  parseAmount(bookedPlotData.agent_commission)
                )}
                {renderDataRow(
                  `Company commission${bookedPlotData.status === "Completed" && " received"}`,
                  parseAmount(bookedPlotData.company_commission)
                )}
                {renderDataRow("Registration fees", parseAmount(bookedPlotData.registration_charges))}
                {renderDataRow("Development charges", parseAmount(bookedPlotData.development_charges))}
              </section>
            </section>
          </section>

          <section className="section_wrapper">
            <section className="section_title_wrapper">
              <h2 className="section_title text-center">Ledger</h2>
            </section>
            <BookingsLedgerTable
              data={ledgerData}
              bookingData={{
                paymentType: bookedPlotData.payment_type,
                emiCount: bookedPlotData.emi_chosen,
                emiValue: bookedPlotData.emi_value,
                token: bookedPlotData.token,
                downPayment: bookedPlotData.down_payment,
              }}
            />
          </section>
        </main>
      )}
    </>
  );
}

export default BookingsPage;
